import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { catchError, concat, filter, mapTo, merge, Observable, of, switchMap, tap } from 'rxjs';
import { SmoEnvironment } from '../../../../Models/Smo/SmoEnvironment';
import { User } from '../../../../Models/User/User';
import { mapPayload } from '../../../../Utils/RxjsOperators/MapPayload';
import { Optional, TwitchToken } from '../../../../Utils/Types';
import { AuthenticationManagerRepresentable } from '../../../AuthenticationManager/AuthenticationManager.interface';
import { ImageClassifier } from '../../../ImageClassifier/ImageClassifier';
import { SmoMoonPrediction } from '../../../ImageClassifier/Models/SmoMoonPrediction';
import { Request } from '../../../Request/Request';
import { SmoSocketClientEvent } from '../../../Socket/Models/SmoSocketClientEvent';
import { SocketManager } from '../../../Socket/SocketManager';
import ServiceStoreActions from '../../Global/Service/ServiceStoreActions';
import { StoreDependencies } from '../../Global/StoreDependencies';
import { StoreState } from '../../Global/StoreState';
import TextReaderActions from '../../Global/TextReader/TextReaderActions';
import UserStoreActions from '../../Global/User/UserStoreActions';
import WidgetFrontActions from '../WidgetFront/WidgetFrontActions';
import AuthPageActions from './AuthPageActions';

function authenticate(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(AuthPageActions.authenticate.match),
    mapPayload(),
    tap((token) => Request.createInstanceWithToken(token)),
    switchMap((token) => getCurrentUser(token, dependencies.authenticationManager!)),
    switchMap((user) =>
      concat(
        of(UserStoreActions.setCurrentUser(user)),
        merge(
          connectWebsocket(user, $state.value.videoAnalysis.predictedEnvironment, dependencies.socketManager!),
          loadSmoEnvironmentImageClassifier(dependencies.smoEnvironmentImageClassifier!),
          loadSmoMultiMoonImageClassifier(dependencies.smoMultiMoonImageClassifier!),
          of(TextReaderActions.loadTextReader())
        )
      )
    )
  );
}

function getCurrentUser(
  token: TwitchToken,
  authenticationManager: AuthenticationManagerRepresentable
): Observable<User> {
  return authenticationManager.authenticate(token);
}

function loadSmoEnvironmentImageClassifier(
  smoEnvironmentImageClassifier: ImageClassifier<SmoEnvironment>
): Observable<AnyAction> {
  return smoEnvironmentImageClassifier.load().pipe(
    mapTo(ServiceStoreActions.setIsSmoEnvironmentImageClassifierLoaded(true)),
    catchError(() => of(ServiceStoreActions.setIsSmoEnvironmentImageClassifierLoaded(false)))
  );
}

function loadSmoMultiMoonImageClassifier(
  smoMoonTypeImageClassifier: ImageClassifier<SmoMoonPrediction>
): Observable<AnyAction> {
  return smoMoonTypeImageClassifier.load().pipe(
    mapTo(ServiceStoreActions.setIsSmoMoonTypeImageClassifierLoaded(true)),
    catchError(() => of(ServiceStoreActions.setIsSmoMoonTypeImageClassifierLoaded(false)))
  );
}

function connectWebsocket(
  user: User,
  environment: Optional<SmoEnvironment>,
  socketManager: SocketManager
): Observable<AnyAction> {
  return socketManager
    .emit$(SmoSocketClientEvent.connect_front, { userId: user.id })
    .pipe(
      switchMap(() => [
        ServiceStoreActions.setIsWebsocketConnected(true),
        WidgetFrontActions.observeWidgetConnection(),
        WidgetFrontActions.updateSettings(user.settings.widget),
        WidgetFrontActions.updateRun(user.run),
        WidgetFrontActions.updateEnvironment(environment ?? SmoEnvironment.none)
      ])
    );
}

export default combineEpics(authenticate);
